import React, { useState, useEffect, useCallback } from 'react';
import {
  Cpu, FileText, FolderInput, CheckCircle, XCircle,
  RefreshCw, ChevronRight, Eye, AlertTriangle, Clock,
  Target, Brain, BarChart2, Loader2, Edit2, Save
} from 'lucide-react';
import api from '../../services/api';

/**
 * ScanProcessor component - AI-powered document classification with training feedback
 */
const ScanProcessor = ({ onDocumentFiled }) => {
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [classifications, setClassifications] = useState([]);
  const [overview, setOverview] = useState(null);
  const [trainingStats, setTrainingStats] = useState(null);
  const [selectedScan, setSelectedScan] = useState(null);
  const [cases, setCases] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editMode, setEditMode] = useState(null);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [pendingRes, overviewRes, statsRes, casesRes, categoriesRes] = await Promise.all([
        api.get('/scan-processing/pending'),
        api.get('/scan-processing/stats/overview'),
        api.get('/scan-processing/stats/training'),
        api.get('/scan-processing/cases'),
        api.get('/scan-processing/categories')
      ]);

      setClassifications(pendingRes.data.classifications || []);
      setOverview(overviewRes.data);
      setTrainingStats(statsRes.data);
      setCases(casesRes.data || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (err) {
      console.error('Failed to load scan data:', err);
    } finally {
      setLoading(false);
    }
  };

  const processBatch = async () => {
    try {
      setProcessing(true);
      await api.post('/scan-processing/process-batch?batch_size=10');
      await loadData();
    } catch (err) {
      console.error('Batch processing failed:', err);
      alert('Failed to process scans. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const loadScanDetail = async (classification) => {
    try {
      const res = await api.get(`/scan-processing/${classification.id}`);
      setSelectedScan(res.data);
    } catch (err) {
      console.error('Failed to load scan detail:', err);
    }
  };

  const startEdit = (classification) => {
    setEditMode(classification.id);
    setEditData({
      category: classification.ai_category,
      subcategory: classification.ai_subcategory,
      case_id: classification.ai_suggested_case_id,
      filename: classification.ai_suggested_filename
    });
  };

  const submitFeedback = async (classificationId, action = 'approve') => {
    try {
      const payload = {
        action,
        ...editData
      };

      await api.post(`/scan-processing/${classificationId}/feedback`, payload);
      setEditMode(null);
      setEditData({});
      await loadData();
    } catch (err) {
      console.error('Feedback submission failed:', err);
      alert('Failed to submit feedback');
    }
  };

  const fileDocument = async (classificationId) => {
    try {
      await api.post(`/scan-processing/${classificationId}/file`);
      await loadData();
      if (onDocumentFiled) onDocumentFiled();
    } catch (err) {
      console.error('Filing failed:', err);
      alert(err.response?.data?.detail || 'Failed to file document');
    }
  };

  const bulkFile = async () => {
    const reviewedIds = classifications
      .filter(c => c.status === 'reviewed')
      .map(c => c.id);

    if (reviewedIds.length === 0) {
      alert('No reviewed documents ready for filing');
      return;
    }

    try {
      const res = await api.post('/scan-processing/bulk-file', {
        classification_ids: reviewedIds
      });
      alert(`Filed ${res.data.filed} documents. ${res.data.failed} failed.`);
      await loadData();
      if (onDocumentFiled) onDocumentFiled();
    } catch (err) {
      console.error('Bulk file failed:', err);
      alert('Bulk filing failed');
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.8) return 'text-green-400';
    if (confidence >= 0.5) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 bg-yellow-900/50 text-yellow-300 text-xs rounded">Pending Review</span>;
      case 'reviewed':
        return <span className="px-2 py-1 bg-blue-900/50 text-blue-300 text-xs rounded">Reviewed</span>;
      case 'filed':
        return <span className="px-2 py-1 bg-green-900/50 text-green-300 text-xs rounded">Filed</span>;
      case 'rejected':
        return <span className="px-2 py-1 bg-red-900/50 text-red-300 text-xs rounded">Rejected</span>;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-virgo-teal-400" size={32} />
        <span className="ml-2 text-virgo-teal-400">Loading scan processor...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-virgo-teal-500 text-sm">Unprocessed</p>
              <p className="text-2xl font-bold text-white">{overview?.counts?.unprocessed || 0}</p>
            </div>
            <Clock className="text-yellow-400" size={24} />
          </div>
        </div>
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-virgo-teal-500 text-sm">Pending Review</p>
              <p className="text-2xl font-bold text-white">{overview?.counts?.pending || 0}</p>
            </div>
            <Eye className="text-blue-400" size={24} />
          </div>
        </div>
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-virgo-teal-500 text-sm">Reviewed</p>
              <p className="text-2xl font-bold text-white">{overview?.counts?.reviewed || 0}</p>
            </div>
            <CheckCircle className="text-green-400" size={24} />
          </div>
        </div>
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-virgo-teal-500 text-sm">Filed</p>
              <p className="text-2xl font-bold text-white">{overview?.counts?.filed || 0}</p>
            </div>
            <FolderInput className="text-cyan-400" size={24} />
          </div>
        </div>
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-virgo-teal-500 text-sm">AI Accuracy</p>
              <p className="text-2xl font-bold text-white">{trainingStats?.accuracy_rate || 0}%</p>
            </div>
            <Brain className="text-virgo-teal-400" size={24} />
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center">
        <div className="flex space-x-3">
          <button
            onClick={processBatch}
            disabled={processing || (overview?.counts?.unprocessed === 0)}
            className="flex items-center px-4 py-2 bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 disabled:opacity-50"
          >
            {processing ? (
              <>
                <Loader2 size={18} className="animate-spin mr-2" />
                Processing...
              </>
            ) : (
              <>
                <Cpu size={18} className="mr-2" />
                Process Scans with AI
              </>
            )}
          </button>

          {(overview?.counts?.reviewed || 0) > 0 && (
            <button
              onClick={bulkFile}
              className="flex items-center px-4 py-2 bg-virgo-teal-600 text-white rounded-lg hover:bg-virgo-teal-700"
            >
              <FolderInput size={18} className="mr-2" />
              File All Reviewed ({overview?.counts?.reviewed})
            </button>
          )}
        </div>

        <button
          onClick={loadData}
          className="flex items-center px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500"
        >
          <RefreshCw size={18} className="mr-2" />
          Refresh
        </button>
      </div>

      {/* Classifications List */}
      {classifications.length === 0 ? (
        <div className="bg-virgo-navy-700 rounded-lg p-8 text-center border border-virgo-teal-800">
          <FileText size={48} className="mx-auto text-virgo-teal-600 mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">No Scans to Review</h3>
          <p className="text-virgo-teal-500">
            {overview?.counts?.unprocessed > 0
              ? `${overview.counts.unprocessed} unprocessed scan(s) waiting. Click "Process Scans with AI" to classify them.`
              : 'All scans have been processed and filed.'}
          </p>
        </div>
      ) : (
        <div className="bg-virgo-navy-700 rounded-lg border border-virgo-teal-800 overflow-hidden">
          <div className="px-4 py-3 bg-virgo-navy-800 border-b border-virgo-teal-800">
            <h3 className="font-medium text-white">Scans Pending Review ({classifications.length})</h3>
          </div>

          <div className="divide-y divide-virgo-teal-800">
            {classifications.map(classification => (
              <div
                key={classification.id}
                className={`p-4 hover:bg-virgo-navy-600 ${editMode === classification.id ? 'bg-virgo-navy-600' : ''}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <FileText size={18} className="text-virgo-teal-500 mr-2" />
                      <span className="font-medium text-white">{classification.original_filename}</span>
                      <span className="ml-3">{getStatusBadge(classification.status)}</span>
                    </div>

                    {editMode === classification.id ? (
                      /* Edit Mode */
                      <div className="space-y-3 mt-4 bg-virgo-navy-800 p-4 rounded-lg">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-virgo-teal-400 mb-1">Category</label>
                            <select
                              value={editData.category || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                              className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white"
                            >
                              <option value="">Select Category</option>
                              {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-virgo-teal-400 mb-1">Subcategory</label>
                            <select
                              value={editData.subcategory || ''}
                              onChange={(e) => setEditData(prev => ({ ...prev, subcategory: e.target.value }))}
                              className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white"
                            >
                              <option value="">Select Subcategory</option>
                              {categories.find(c => c.value === editData.category)?.subcategories?.map(sub => (
                                <option key={sub} value={sub}>{sub}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm text-virgo-teal-400 mb-1">File to Case</label>
                          <select
                            value={editData.case_id || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, case_id: e.target.value }))}
                            className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white"
                          >
                            <option value="">Select Case</option>
                            {cases.map(c => (
                              <option key={c.id} value={c.id}>
                                {c.client_name} - {c.case_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm text-virgo-teal-400 mb-1">Filename</label>
                          <input
                            type="text"
                            value={editData.filename || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, filename: e.target.value }))}
                            className="w-full px-3 py-2 bg-virgo-navy-700 border border-virgo-teal-700 rounded-lg text-white"
                            placeholder="Document filename"
                          />
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => submitFeedback(classification.id, 'approve')}
                            className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                          >
                            <CheckCircle size={16} className="mr-2" />
                            Approve
                          </button>
                          <button
                            onClick={() => submitFeedback(classification.id, 'reject')}
                            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            <XCircle size={16} className="mr-2" />
                            Reject
                          </button>
                          <button
                            onClick={() => { setEditMode(null); setEditData({}); }}
                            className="px-4 py-2 bg-virgo-navy-600 text-virgo-teal-300 rounded-lg hover:bg-virgo-navy-500"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="grid grid-cols-4 gap-4 text-sm mt-2">
                          <div>
                            <span className="text-virgo-teal-500">Category: </span>
                            <span className="text-white capitalize">{classification.ai_category}</span>
                          </div>
                          <div>
                            <span className="text-virgo-teal-500">Type: </span>
                            <span className="text-white capitalize">{classification.ai_subcategory}</span>
                          </div>
                          <div>
                            <span className="text-virgo-teal-500">Confidence: </span>
                            <span className={getConfidenceColor(classification.ai_confidence)}>
                              {Math.round((classification.ai_confidence || 0) * 100)}%
                            </span>
                          </div>
                          <div>
                            <span className="text-virgo-teal-500">Case: </span>
                            <span className="text-white">{classification.suggested_case_name || 'None'}</span>
                          </div>
                        </div>

                        {classification.ai_summary && (
                          <p className="text-sm text-virgo-teal-400 mt-2 line-clamp-2">
                            {classification.ai_summary}
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  {editMode !== classification.id && (
                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => window.open(`/api/scan-processing/${classification.id}/preview`, '_blank')}
                        className="p-2 text-virgo-teal-400 hover:bg-virgo-navy-600 rounded"
                        title="Preview"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => startEdit(classification)}
                        className="p-2 text-blue-400 hover:bg-virgo-navy-600 rounded"
                        title="Review & Edit"
                      >
                        <Edit2 size={18} />
                      </button>
                      {classification.status === 'reviewed' && (
                        <button
                          onClick={() => fileDocument(classification.id)}
                          className="p-2 text-green-400 hover:bg-virgo-navy-600 rounded"
                          title="File to Case"
                        >
                          <FolderInput size={18} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Training Stats */}
      {trainingStats && (trainingStats.total_processed > 0) && (
        <div className="bg-virgo-navy-700 rounded-lg p-4 border border-virgo-teal-800">
          <h3 className="font-medium text-white mb-4 flex items-center">
            <BarChart2 size={18} className="mr-2 text-virgo-teal-400" />
            AI Training Statistics
          </h3>
          <div className="grid grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-virgo-teal-500">Total Processed: </span>
              <span className="text-white font-medium">{trainingStats.total_processed}</span>
            </div>
            <div>
              <span className="text-virgo-teal-500">AI Accurate: </span>
              <span className="text-green-400 font-medium">{trainingStats.accurate}</span>
            </div>
            <div>
              <span className="text-virgo-teal-500">Human Corrections: </span>
              <span className="text-yellow-400 font-medium">{trainingStats.corrections}</span>
            </div>
            <div>
              <span className="text-virgo-teal-500">Overall Accuracy: </span>
              <span className="text-white font-medium">{trainingStats.accuracy_rate}%</span>
            </div>
          </div>

          {trainingStats.by_category?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-virgo-teal-800">
              <p className="text-virgo-teal-500 text-sm mb-2">Documents by Category:</p>
              <div className="flex flex-wrap gap-2">
                {trainingStats.by_category.map(cat => (
                  <span key={cat.category} className="px-2 py-1 bg-virgo-navy-600 rounded text-sm">
                    <span className="text-white capitalize">{cat.category}: </span>
                    <span className="text-virgo-teal-400">{cat.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ScanProcessor;
